import React, {useState, useEffect, useRef, useMemo} from "react";
import "./slider.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faClock,
    faSquareFull,
    faUser, faUserFriends,
    faUserGroup, faUsers
} from "@fortawesome/free-solid-svg-icons";
import {ButtonConfig, Game, GameTextData, getTextByLang} from "./Game";
import {Flex} from "antd";
import {getLangFromUrl} from "./Header";
import {useNavigate} from "react-router-dom";
import {useSmartNavigate} from "./Main";

// Must match the transition duration defined in slider.css
const TRANSITION_MS = 500;

export const Slider = (props: {
    data: Game[];
    activeSlide: number;
    /** Total number of side cards visible (split equally left and right). Total visible = visibleCount + 1. Defaults to 4. */
    visibleCount?: number;
    /** Width and height of each slide in px. Defaults to { width: 362, height: 272 }. */
    size?: { width: number; height: number };
    /** Desired width of the slider area in px. Scales down proportionally when screen is narrower. */
    containerWidth?: number;
    /** Autoplay interval in ms. Omit or set to 0 to disable. */
    autoPlay?: number;
    /** Loop back to the first slide after the last one. */
    loop?: boolean;
    /** Enable right-to-left layout (mirrors slide positions, swipe direction and arrow icons). */
    rtl?: boolean;
}) => {
    const lang = getLangFromUrl();
    const navigate = useSmartNavigate();
    const rtl = props.rtl ?? false;
    const visibleCount = props.visibleCount ?? 4;
    const sideCount = Math.floor(visibleCount / 2);
    const rawContainerWidth = props.containerWidth ?? 800;
    const rawSlideWidth = props.size?.width ?? 362;
    const rawSlideHeight = props.size?.height ?? 272;
    const count = props.data.length;

    // activeSlide is an unbounded counter for loop mode (modulo maps it to data),
    // or bounded to [0, count-1] for non-loop mode.
    const [activeSlide, setActiveSlide] = useState(Math.round(props.activeSlide));

    // Measure the slider wrapper width (excludes button widths via flex layout).
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [wrapperWidth, setWrapperWidth] = useState(() =>
        Math.min(rawContainerWidth, typeof window !== 'undefined' ? window.innerWidth : rawContainerWidth)
    );
    useEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        // Ignore zero-width readings that occur while the element is inside a
        // display:none ancestor — the last known non-zero width is kept so the
        // slide geometry stays valid until the element becomes visible again.
        const update = () => { if (el.offsetWidth > 0) setWrapperWidth(el.offsetWidth); };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const scale = Math.min(1, wrapperWidth / rawContainerWidth);
    const containerWidth = rawContainerWidth * scale;
    const slideWidth = rawSlideWidth * scale;
    const slideHeight = rawSlideHeight * scale;

    // On narrow containers reduce visible side cards to avoid overcrowding
    const effectiveSideCount = wrapperWidth < 300 ? 0 : wrapperWidth < 550 ? Math.min(sideCount, 1) : sideCount;

    // Geometry arrays depend only on dimensions — memoize to avoid recalculating on every slide advance.
    const {slideXRight, slideXLeft} = useMemo(() => {
        // Perspective value must match .slideC { perspective: 1000px }
        const P = 1000;
        const xOrigin = containerWidth / 2;
        const baseX = (containerWidth - slideWidth) / 2;
        const sumOfRatios = (effectiveSideCount * (effectiveSideCount + 1)) / 2;

        const peeks: number[] = [0];
        for (let k = 1; k <= effectiveSideCount; k++) {
            peeks[k] = ((effectiveSideCount - k + 1) / sumOfRatios) * baseX;
        }

        const right: number[] = [baseX];
        const left: number[] = [baseX];
        let apparentRight = baseX + slideWidth;
        for (let k = 1; k <= effectiveSideCount; k++) {
            const z = 400 + (k - 1) * 100;
            apparentRight += peeks[k];
            const ax = xOrigin + (apparentRight - xOrigin) * (P + z) / P - slideWidth;
            right[k] = ax;
            left[k] = containerWidth - slideWidth - ax;
        }

        return {slideXRight: right, slideXLeft: left};
    }, [containerWidth, slideWidth, effectiveSideCount]);

    const baseX = (containerWidth - slideWidth) / 2;

    const next = () => {
        if (props.loop) setActiveSlide(s => s + 1);
        else if (activeSlide < count - 1) setActiveSlide(s => s + 1);
    };
    const prev = () => {
        if (props.loop) setActiveSlide(s => s - 1);
        else if (activeSlide > 0) setActiveSlide(s => s - 1);
    };

    const touchStartX = useRef<number | null>(null);
    const onTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const delta = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 50) (delta > 0) !== rtl ? next() : prev();
        touchStartX.current = null;
    };

    useEffect(() => {
        if (!props.autoPlay) return;
        const id = setInterval(next, props.autoPlay);
        return () => clearInterval(id);
    }, [activeSlide, props.autoPlay, props.loop]);

    const getStyles = (pos: number) => {
        const diff = pos - activeSlide;
        const absDiff = Math.abs(diff);
        const side = Math.sign(diff);

        if (absDiff === 0)
            return {
                opacity: 1,
                transform: `translateX(${baseX}px) translateZ(0px) rotateY(0deg)`,
                zIndex: 10
            };

        const visible = absDiff <= effectiveSideCount;
        const k = Math.min(absDiff, effectiveSideCount);
        const z = 400 + (k - 1) * 100;
        // In RTL, higher-indexed slides sit to the LEFT, so flip the lookup
        const ax = (side > 0) !== rtl ? slideXRight[k] : slideXLeft[k];

        return {
            opacity: visible ? 1 : 0,
            transform: `translateX(${ax}px) translateZ(${-z}px) rotateY(0deg)`,
            zIndex: visible ? 10 - absDiff : 7
        };
    };

    // Render a fixed window of virtual positions centred on activeSlide.
    // Key = absolute position so the same DOM node is reused across slides,
    // letting CSS transitions animate the transform change.
    // Data is mapped via modulo for loop mode; out-of-range slots are hidden for non-loop.
    const buffer = effectiveSideCount + 1; // one extra invisible slot on each edge for smooth entry/exit
    const slots = Array.from({length: 2 * (effectiveSideCount + buffer) + 1}, (_, i) => {
        const pos = activeSlide - effectiveSideCount - buffer + i;
        const inRange = props.loop || (pos >= 0 && pos < count);
        const dataIdx = ((pos % count) + count) % count;
        return {pos, item: props.data[dataIdx], styles: getStyles(pos), inRange};
    });

    return (
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
            <div className="btn btn-prev" onClick={prev}>
                <FontAwesomeIcon icon={rtl ? faChevronRight : faChevronLeft} color="#000" size="2x"/>
            </div>
            <div ref={wrapperRef} style={{flex: `0 1 ${rawContainerWidth}px`, minWidth: 0}}>
                <div className="slideC" style={{width: containerWidth, height: slideHeight}}
                     onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
                >
                    {slots.map(({pos, item, styles, inRange}) => (
                        <div
                            key={pos}
                            className="slide"
                            style={{
                                width: slideWidth,
                                height: slideHeight,
                                ...styles,
                                opacity: inRange ? styles.opacity : 0,
                                boxShadow: "0px 20px 40px rgba(48,63,139,0.4)",
                                cursor: 'pointer',
                                pointerEvents: (inRange && styles.opacity > 0) ? 'auto' : 'none',
                            }}
                            onClick={() => {
                                if (pos !== activeSlide) {
                                    setActiveSlide(pos);
                                } else {
                                    navigate("/" + lang + "/" +item.id);
                                }
                            }}
                        ><div style={{width: "100%", height:"100%", border:"1px solid #fff", position:'absolute', zIndex:10, opacity:0.2, borderRadius:28, pointerEvents:'none'}} onClick={(e)=> e.preventDefault()}></div>
                            {inRange && <GameSlide {...item} />}
                        </div>
                    ))}
                </div>
            </div>
            <div className="btn btn-next" onClick={next}>
                <FontAwesomeIcon icon={rtl ? faChevronLeft : faChevronRight} color="#000" size="2x"/>
            </div>
        </div>
    );
};


export function GameSlide(props: Game) {
    const lang = getLangFromUrl();
    const navigate = useSmartNavigate()
    return (
        <div className="game-slide">
            <div className="game-slide__media">
                <img className="game-slide__image" src={getTextByLang(props.image,lang)} alt={getTextByLang(props.name,lang)} draggable={false}/>
                <div className="game-slide__content">
                    <div className="game-slide__footer">
                        <Flex className="game-slide__title" justify="space-between" style={{width: "100%"}} align={'center'}>
                            <div>{getTextByLang(props.name, lang)}</div>
                            <Flex gap={10} className="game-slide__title_tags">
                                {props.players && <InfoPill icon={<FontAwesomeIcon icon={faUsers} />} text={getTextByLang(props.players,lang)}/>}
                                {props.duration && <InfoPill icon={<FontAwesomeIcon icon={faClock} />} text={getTextByLang(props.duration,lang)}/>}
                                {props.age && <InfoPill icon={<FontAwesomeIcon icon={faUserFriends}  />} text={getTextByLang(props.age,lang)}/>}
                            </Flex>
                        </Flex>
                        <div className="game-slide__meta">
                            <div className="game-slide__text">
                                {getTextByLang(props.description,lang) && <p className="game-slide__subtitle">{getTextByLang(props.description,lang)}</p>}
                            </div>
                            <Flex gap={10}>
                                {props.hero?.buttons?.map((btn) => (
                                    <button
                                        key={getTextByLang(btn.link, lang)}
                                        type="button"
                                        className={btn.type === 'secondary' ? 'game-slide__secondary' : 'game-slide__primary'}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(getTextByLang(btn.link, lang));
                                        }}
                                        aria-label={getTextByLang(btn.text,lang)}
                                    >{getTextByLang(btn.text,lang)}</button>
                                ))}
                            </Flex>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function InfoPill({icon, text}: {icon: React.ReactNode; text: string}) {
    return (
        <Flex align="center" gap={5}
              style={{padding: "5px 10px", background: "#444444", borderRadius: 50, color: "#aaa"}}>
            <span>{icon}</span>
            <span>{text}</span>
        </Flex>
    );
}